import { Link } from '@tanstack/react-router';
import { Fragment } from 'react';
import { linkTarget } from '@/routes/link-target';
import { IconChevronRight } from './icons';
import { cn, focusRing } from './utils';

export interface BreadcrumbItem {
  readonly label: string;
  /** Ausente = item corrente (último), renderizado como texto. */
  readonly to?: string;
  readonly params?: Record<string, string>;
}

/**
 * Trilha de navegação.
 *
 * ACESSIBILIDADE: `<nav aria-label="Trilha de navegação">` + `<ol>`, e o item
 * atual leva `aria-current="page"` — é assim que o leitor de tela distingue
 * "onde estou" de "para onde posso ir". Os separadores são `aria-hidden`,
 * senão viram ruído lido a cada item.
 */
export function Breadcrumb({
  items,
  className,
}: {
  readonly items: readonly BreadcrumbItem[];
  readonly className?: string;
}) {
  if (items.length === 0) return null;

  return (
    <nav aria-label="Trilha de navegação" className={cn('min-w-0', className)}>
      <ol className="flex min-w-0 flex-wrap items-center gap-1 text-sm text-content-muted">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <Fragment key={`${item.label}-${index}`}>
              <li className="flex min-w-0 items-center">
                {isLast || !item.to ? (
                  <span
                    aria-current={isLast ? 'page' : undefined}
                    className="truncate font-medium text-content"
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    {...linkTarget(item.to, item.params)}
                    /*
                     * O `<Link>` do TanStack Router marca `aria-current="page"`
                     * sozinho, por prefixo (`STATIC_ACTIVE_PROPS` em link.js).
                     * Num rastro de navegação isso é sempre errado: todo link
                     * daqui é ANCESTRAL — o item atual é o `<span>` acima —, e
                     * `/documentos` casa com `/documentos/administracao`. O
                     * resultado era um rastro que anunciava duas páginas atuais.
                     *
                     * Só `activeOptions.exact` resolve: `activeProps` vazio não
                     * adianta, porque `link.js:198` faz
                     * `...isActive && STATIC_ACTIVE_PROPS` depois de tudo. O que
                     * se desliga é o casamento, não a consequência dele.
                     */
                    activeOptions={{ exact: true }}
                    className={cn('truncate rounded hover:text-brand hover:underline', focusRing)}
                  >
                    {item.label}
                  </Link>
                )}
              </li>
              {!isLast ? (
                <li aria-hidden="true" className="flex items-center text-content-subtle">
                  <IconChevronRight size={14} />
                </li>
              ) : null}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
